export function trimTableData(data) {
  // create a new copy of the data called dataCopy
  let dataCopy = JSON.parse(JSON.stringify(data));
  try {
    // data is a list of objects where key is a table column and value is the cell value
    // this functions uses regex to remove the following from each of the cell values
    // 1. identify patterns matching "[digit.digit]" and replace with empty string. Examples [1002.3], [2234.4] and so on
    // 2. remove leading and trailing whitespaces

    // remove commas appearing between "]" and "[". don't remove the brackets in this regex, just the comma
    const regex1 = /\](\s*),(\s*)\[/g;
    const regex2 = /\[\d+\.\d+\]/g;
    const regex3 = /^\s+|\s+$/g;

    // add a regex that matches the following pattern:
    // if there are openning and closing round brackets and "calculate as" is witin the the brackets, then remove the brackets and the text within the brackets
    // calculated as should be case insensitive
    // there can be other text other than "calculate as" within the brackets
    const regex4 = /\([^()]*\bcalculated\b[^()]*(?:\([^()]*\)[^()]*)*\)/gi;

    // if there is columns named "Source" or "Sources", then remove that column altogether. check case insensitive
    const headers = Object.keys(dataCopy[0]);
    const sourceIndex = headers.findIndex(
      (header) =>
        header.toLowerCase() === "source" || header.toLowerCase() === "sources"
    );
    if (sourceIndex !== -1) {
      dataCopy = dataCopy.map((row) => {
        delete row[headers[sourceIndex]];
        return row;
      });
    }

    return dataCopy.map((row) => {
      const newRow = {};
      Object.keys(row).forEach((key) => {
        newRow[key] = row[key]
          .replace(regex1, "] [")
          .replace(regex2, "")
          .replace(regex3, "")
          .replace(regex4, "");
      });
      return newRow;
    });
  } catch (error) {
    return dataCopy;
  }
}

function getUnit(text) {
  // extracts the unit and returns "K", "M", "B", "T" or ""
  // if text contains "<digit><optional space>K", case insensitive "thousand", then return "K"
  // if text contains "<digit><optional space>M", case insensitive "million", then return "M"
  // if text contains "<digit><optional space>B", case insensitive "billion", then return "B"
  // if text contains "<digit><optional space>T", case insensitive "trillion", then return "T"
  // else return empty string

  const regex1 = /\d+\s*K/g;
  const regex2 = /\d+\s*M/g;
  const regex3 = /\d+\s*B/g;
  const regex4 = /\d+\s*T/g;
  const regex5 = /\d+\s*thousand/gi;
  const regex6 = /\d+\s*million/gi;
  const regex7 = /\d+\s*billion/gi;
  const regex8 = /\d+\s*trillion/gi;

  if (regex1.test(text) || regex5.test(text)) {
    return "K";
  }

  if (regex2.test(text) || regex6.test(text)) {
    return "M";
  }

  if (regex3.test(text) || regex7.test(text)) {
    return "B";
  }

  if (regex4.test(text) || regex8.test(text)) {
    return "T";
  }

  if (text.includes("N/A")) {
    return "N/A";
  }

  return "NO_UNIT";
}

function removeUnit(text, unitSymbol) {
  try {
    let patternText, patternUnit, patternUnitToReplace;
    switch (unitSymbol) {
      case "K":
        patternText = /\b(thousand|thousands)\b/gi;
        patternUnit = /(\d+\s*?K\b)/gi;
        break;
      case "M":
        patternText = /\b(million|millions)\b/gi;
        patternUnit = /(\d+\s*?M\b)/gi;
        break;
      case "B":
        patternText = /\b(billion|billions)\b/gi;
        patternUnit = /(\d+\s*?B\b)/gi;
        break;
      case "T":
        patternText = /\b(trillion|trillions)\b/gi;
        patternUnit = /(\d+\s*?T\b)/gi;
        break;
      default:
        return text; // If an unexpected unit symbol is provided, return the original text
    }

    // Remove specified acceptable patterns
    let cleanText = text.replace(/\(Q\)|\(A\)|\[\d+\.\d+\]/g, "");

    // Remove unit patterns
    cleanText = cleanText.replace(patternText, "").replace(patternUnit, "");

    // Check if the cleaned text contains any alphabetic characters
    if (/[a-zA-Z]/.test(cleanText)) {
      return text; // If there are any alphabetic characters remaining, return the original text
    }

    // first remove patternText
    let newText = text.replace(patternText, "");
    // for patternUnit, you only need to remove the unit symbol and not the patternUnit
    if (patternUnit.test(newText)) {
      patternUnitToReplace = new RegExp(`\\s*${unitSymbol}`, "gi");
      newText = newText.replace(patternUnitToReplace, "");
    }
    return newText.trim();
  } catch (error) {
    console.error("Error in removeUnit:", error);
    return text; // Return the original text in case of an error
  }
}

export function organizeTableUnits(data, orgHeaders) {
  try {
    const headers = Object.keys(data[0]);
    const headersChangeMap = {};
    headers.forEach((header) => {
      headersChangeMap[header] = header;
    });
    // console.log(headers);

    // create an object where keys are headers and values is an array of objects. Each object has the following keys
    // 1. value: the cell value as a string
    // 2. unit: the unit of the cell value.
    // extract the unit using the function getUnit
    const columnOrganizedData = {};
    headers.forEach((header) => {
      columnOrganizedData[header] = [];
    });

    data.forEach((row) => {
      headers.forEach((header) => {
        const unit = getUnit(row[header]);
        columnOrganizedData[header].push({ value: row[header], unit });
      });
    });

    // for each header, extract the unique units
    const uniqueUnitsColumn = {};
    headers.forEach((header) => {
      uniqueUnitsColumn[header] = [
        ...new Set(columnOrganizedData[header].map((cell) => cell.unit)),
      ];
      // remove "N/A" from the unique units
      uniqueUnitsColumn[header] = uniqueUnitsColumn[header].filter(
        (unit) => unit !== "N/A" && unit !== "NO_UNIT"
      );
    });

    // determine if all headers have exactly one unit so we can update the headers. If any header has more than one unit, then we do not update the headers
    let isTableColumnOrganized = false;
    if (headers.every((header) => uniqueUnitsColumn[header].length <= 1)) {
      isTableColumnOrganized = true;
    }

    // if for each header, there is only one unit by checking the length, then update the headers by adding the unit to the header name
    // for example, `header ${unit}`
    // then update data where the key is updated with the header value with the unit

    let updatedData = [];

    if (isTableColumnOrganized) {
      // console.log("Table is column organized", data);
      data.forEach((row) => {
        const newRow = {};
        headers.forEach((header) => {
          if (uniqueUnitsColumn[header][0] !== "NO_UNIT") {
            newRow[
              `${header} ${uniqueUnitsColumn[header][0] ? "(" + uniqueUnitsColumn[header][0] + ")" : ""}`
            ] = removeUnit(row[header], uniqueUnitsColumn[header][0]);
            headersChangeMap[header] =
              `${header} ${uniqueUnitsColumn[header][0] ? "(" + uniqueUnitsColumn[header][0] + ")" : ""}`;
          } else {
            newRow[header] = row[header];
            headersChangeMap[header] = header;
          }
        });
        updatedData.push(newRow);
      });
      // console.log("Updated data", updatedData);
    } else {
      // we check if the table is row organized and check for units row-wise

      // create an object where keys are first column values and values is an array of objects. Each object has the following keys
      // 1. value: the cell value as a string
      // 2. unit: the unit of the cell value.
      // extract the unit using the function getUnit
      const rowOrganizedData = {};
      const firstHeader = headers[0];
      data.forEach((row) => {
        rowOrganizedData[row[firstHeader]] = [];
      });

      data.forEach((row) => {
        headers.forEach((header) => {
          const unit = getUnit(row[header]);
          rowOrganizedData[row[firstHeader]].push({ value: row[header], unit });
        });
      });

      // for each row, extract the unique units, but skip the first column as it is the key
      const uniqueUnitsRow = {};
      Object.keys(rowOrganizedData).forEach((key) => {
        // remove the first column as it is the key
        uniqueUnitsRow[key] = [
          ...new Set(rowOrganizedData[key].slice(1).map((cell) => cell.unit)),
        ];

        // remove "N/A" from the unique units
        uniqueUnitsRow[key] = uniqueUnitsRow[key].filter(
          (unit) => unit !== "N/A"
        );
      });

      // determine if all rows have exactly one unit so we can update the headers. If any row has more than one unit, then we do not update the headers
      let isTableRowOrganized = false;
      if (
        Object.keys(rowOrganizedData).every(
          (key) => uniqueUnitsRow[key].length === 1
        )
      ) {
        isTableRowOrganized = true;
      }

      // if for each row, there is only one unit by checking the length, then update the first column with the unit
      // for example, `firstColumn ${unit}`
      if (isTableRowOrganized) {
        data.forEach((row) => {
          const newRow = {};
          headers.forEach((header) => {
            const rowUnit = uniqueUnitsRow[row[firstHeader]][0];
            if (rowUnit !== "NO_UNIT") {
              // the row needs to be updated with the unit where unit is added to the first column but removed from the other columns

              if (header === firstHeader) {
                newRow[header] =
                  `${row[header]} ${rowUnit ? "(" + rowUnit + ")" : ""}`;
              } else {
                newRow[header] = removeUnit(row[header], rowUnit);
              }
            } else {
              newRow[header] = row[header];
            }
          });
          updatedData.push(newRow);
        });
      }
    }

    if (updatedData.length === 0) {
      return { data, headersChangeMap };
    } else {
      return { data: updatedData, headersChangeMap };
    }
  } catch (error) {
    return { data, headersChangeMap: null };
  }
}
